// ─────────────────────────────────────────────────────────────────────────────
// Tiny spring engine (no dependencies), parameterised the way Apple does it:
//   response      — seconds to (roughly) reach the target. NOT a duration.
//   dampingRatio  — 1.0 = critically damped (no overshoot), < 1 = bounce.
// Every animation starts from the CURRENT value and CURRENT velocity, so any
// motion can be grabbed / re-targeted mid-flight without a jump.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";

export const prefersReducedMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
};

// Apple's momentum projection (Designing Fluid Interfaces sample code).
export const project = (velocityPxPerSec, decelerationRate = 0.998) =>
  ((velocityPxPerSec / 1000) * decelerationRate) / (1 - decelerationRate);

// Progressive resistance past a boundary.
export const rubberband = (overshoot, dimension, c = 0.55) =>
  (overshoot * dimension * c) / (dimension + c * Math.abs(overshoot));

export class Spring {
  constructor(value = 0, { response = 0.4, dampingRatio = 1, onUpdate, onRest } = {}) {
    this.value = value;
    this.target = value;
    this.velocity = 0;
    this.response = response;
    this.dampingRatio = dampingRatio;
    this.onUpdate = onUpdate;
    this.onRest = onRest;
    this._raf = null;
    this._last = 0;
  }

  // Jump without animating (used while a finger is dragging: 1:1 tracking).
  set(value) {
    this.stop();
    this.value = value;
    this.target = value;
    this.velocity = 0;
    this.onUpdate?.(value);
  }

  // Animate to `to`. Optionally hand off a gesture's release velocity (units/s).
  to(to, { velocity, response, dampingRatio } = {}) {
    this.target = to;
    if (velocity !== undefined) this.velocity = velocity;
    if (response !== undefined) this.response = response;
    if (dampingRatio !== undefined) this.dampingRatio = dampingRatio;
    if (prefersReducedMotion()) { this.set(to); this.onRest?.(to); return; }
    if (!this._raf) {
      this._last = performance.now();
      this._raf = requestAnimationFrame(this._tick);
    }
  }

  stop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
  }

  _tick = (now) => {
    // Clamp long frames (background tab) so the integration stays stable.
    let dt = Math.min((now - this._last) / 1000, 1 / 30);
    this._last = now;
    const k = Math.pow((2 * Math.PI) / this.response, 2); // stiffness (mass = 1)
    const c = (4 * Math.PI * this.dampingRatio) / this.response; // damping
    // Fixed sub-steps for accuracy at any frame rate.
    const step = 1 / 240;
    while (dt > 0) {
      const h = Math.min(step, dt);
      const accel = -k * (this.value - this.target) - c * this.velocity;
      this.velocity += accel * h;
      this.value += this.velocity * h;
      dt -= h;
    }
    const settled =
      Math.abs(this.velocity) < 0.01 && Math.abs(this.value - this.target) < 0.01;
    if (settled) {
      this.value = this.target;
      this.velocity = 0;
      this._raf = null;
      this.onUpdate?.(this.value);
      this.onRest?.(this.value);
      return;
    }
    this.onUpdate?.(this.value);
    this._raf = requestAnimationFrame(this._tick);
  };
}

// A number that springs toward `target` and re-renders as it moves.
// Re-targeting mid-flight continues from the live value — ideal for KPIs.
export function useSpringNumber(target, { response = 0.9, dampingRatio = 1 } = {}) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  if (!ref.current) ref.current = new Spring(0, { response, dampingRatio, onUpdate: setVal });
  useEffect(() => {
    ref.current.to(Number(target) || 0);
  }, [target]);
  useEffect(() => () => ref.current.stop(), []);
  return val;
}

// Velocity tracker for pointer gestures: keeps a short history, not just the last point.
export function createVelocityTracker() {
  let samples = [];
  return {
    reset() { samples = []; },
    add(pos) {
      const t = performance.now();
      samples.push({ pos, t });
      samples = samples.filter((s) => t - s.t < 100);
    },
    velocity() {
      if (samples.length < 2) return 0;
      const a = samples[0];
      const b = samples[samples.length - 1];
      const dt = (b.t - a.t) / 1000;
      return dt > 0 ? (b.pos - a.pos) / dt : 0;
    },
  };
}
